import { useState } from 'react';
import { Toaster } from 'sonner';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import PricingLanding from '@/pages/pricing/PricingLanding';
import LearnFlow   from '@/pages/pricing/LearnFlow';
import DefendFlow  from '@/pages/pricing/DefendFlow';
import LaunchFlow  from '@/pages/pricing/LaunchFlow';
import ReachFlow   from '@/pages/pricing/ReachFlow';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const PRICING_OG_IMAGE = `${SITE_URL}/images/og/og-pricing.png`;
const FLOW_SERVICES = ['learn', 'defend', 'launch', 'reach'];

/** Read ?service= from the current URL. Returns a whitelisted slug or null. */
function readServiceFromUrl() {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('service');
  return FLOW_SERVICES.includes(v) ? v : null;
}

/**
 * /pricing — service selector landing + four questionnaire flows.
 * Initial service state hydrates from ?service= on mount so marketing
 * pages can deep-link straight into a specific questionnaire.
 * The flow state lives in this component; back-to-landing resets it
 * AND clears the query string so a refresh on the landing stays on
 * the landing.
 */
export default function PricingFlowPage() {
  const [service, setService] = useState(readServiceFromUrl); // null | 'learn' | 'defend' | 'launch' | 'reach'

  useSEO({
    title: `${service ? `${service.charAt(0).toUpperCase() + service.slice(1)} · Pricing` : 'Pricing'} | Acodia — Grow. Defend. Be Found.`,
    description: 'Answer a few questions and we will show you exactly what we recommend and what it costs. No obligation. No sales pitch. Just a straight answer.',
    path: '/pricing',
    ogImage: PRICING_OG_IMAGE,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://acodia.co.uk/' },
          { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://acodia.co.uk/pricing' },
        ],
      },
    ],
  });

  const exit = () => {
    setService(null);
    // Drop the ?service= param so a refresh on landing stays on landing.
    if (typeof window !== 'undefined' && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  let content;
  if (service === 'learn')       content = <LearnFlow  onExit={exit} />;
  else if (service === 'defend') content = <DefendFlow onExit={exit} />;
  else if (service === 'launch') content = <LaunchFlow onExit={exit} />;
  else if (service === 'reach')  content = <ReachFlow  onExit={exit} />;
  else content = <PricingLanding onSelect={setService} />;

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', color: 'var(--white)' }} data-testid="pricing-flow-page">
      <Navbar />
      <Toaster
        theme="dark"
        position="top-right"
        richColors
        toastOptions={{
          style: {
            background: 'var(--l2)',
            border: '1px solid var(--b2)',
            color: 'var(--white)',
            fontFamily: 'var(--fb)',
          },
        }}
      />
      <main>{content}</main>
      <Footer />
    </div>
  );
}
